import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AlertDialog,
  Body,
  Button,
  Card,
  Eyebrow,
  PageHeader,
  Row,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { groupKeys } from '../../../lib/queries/groups';
import { spacesKeys } from '../../../lib/queries/spaces';
import {
  useArchiveGroup,
  useRestoreGroup,
} from '../../../lib/mutations/groups';
import { AdminFormPageSkeleton } from './AdminFormPageSkeleton';

type GroupKind = 'circle' | 'network' | 'space';

type Props = {
  readonly groupId: string;
  readonly kind: GroupKind;
};

const NOUN: Record<GroupKind, string> = {
  circle: 'Circle',
  network: 'Network',
  space: 'Group',
};

const POST_ARCHIVE_ROUTE: Record<
  GroupKind,
  '/circles/$id' | '/networks/$id' | '/groups/$id'
> = {
  circle: '/circles/$id',
  network: '/networks/$id',
  space: '/groups/$id',
};

type Loaded = {
  readonly groupName: string;
  readonly status: 'active' | 'archived';
};

export function GroupArchiveSection({ groupId, kind }: Props) {
  // Parallel queries, each gated by `kind` via `enabled`. The inactive
  // branches are no-ops; the active one populates `loaded` below.
  const circleQuery = useQuery({
    ...groupKeys.circle.detail(groupId),
    enabled: kind === 'circle',
  });
  const networkQuery = useQuery({
    ...groupKeys.network.detail(groupId),
    enabled: kind === 'network',
  });
  const spaceQuery = useQuery({
    ...spacesKeys.detail(groupId),
    enabled: kind === 'space',
  });

  let loaded: Loaded | null = null;
  if (kind === 'circle') {
    const detail = circleQuery.data;
    if (detail !== undefined && detail.access !== 'visitor') {
      loaded = { groupName: detail.circle.name, status: detail.circle.status };
    }
  } else if (kind === 'network') {
    const detail = networkQuery.data;
    if (detail !== undefined) {
      loaded = { groupName: detail.name, status: detail.status };
    }
  } else {
    const detail = spaceQuery.data;
    if (detail !== undefined && detail !== null) {
      loaded = {
        groupName: detail.name,
        status: detail.archivedAt !== null ? 'archived' : 'active',
      };
    }
  }

  const showSkeleton = useDelayedLoading(loaded === null);
  if (showSkeleton) {
    return <AdminFormPageSkeleton cards={1} rowsPerCard={4} withAction />;
  }
  if (loaded === null) return null;

  return loaded.status === 'archived' ? (
    <RestorePanel groupId={groupId} groupName={loaded.groupName} kind={kind} />
  ) : (
    <ArchivePanel groupId={groupId} groupName={loaded.groupName} kind={kind} />
  );
}

function ArchivePanel({
  groupId,
  groupName,
  kind,
}: {
  groupId: string;
  groupName: string;
  kind: GroupKind;
}) {
  const navigate = useNavigate();
  const archiveMutation = useArchiveGroup();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const noun = NOUN[kind];

  const handleArchive = () => {
    setError(null);
    archiveMutation.mutate(groupId, {
      onSuccess: () => {
        navigate({ to: POST_ARCHIVE_ROUTE[kind], params: { id: groupId } });
      },
      onError: (err) =>
        setError(err instanceof Error ? err.message : `Failed to archive ${noun}.`),
    });
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Archive"
        subtitle={`Archive ${groupName}? Members will lose access immediately. Posts, members, and meetings are preserved — you can restore it later from the archived list. There is no automatic delete.`}
      />

      <Card pad="default">
        <Stack gap="lg">
          <Eyebrow tone="accent">What happens when you archive</Eyebrow>
          <Stack gap="sm">
            <Body size="sm" color="mute">
              · Members lose access to this {noun}
              {kind === 'circle' ? ' and its group chat' : ''}.
            </Body>
            <Body size="sm" color="mute">
              · The {noun} disappears from directories and search.
            </Body>
            <Body size="sm" color="mute">
              · Any leader can restore the {noun} at any time from your archived list.
            </Body>
            <Body size="sm" color="mute">
              · No content is deleted.
            </Body>
          </Stack>
          <Row>
            <Button
              variant="ghostDanger"
              size="md"
              onClick={() => setConfirmOpen(true)}
              loading={archiveMutation.isPending}
            >
              {`Archive ${noun}`}
            </Button>
          </Row>
          {error !== null ? (
            <span role="alert" aria-live="polite">
              <Body size="sm" color="destructive">{error}</Body>
            </span>
          ) : null}
        </Stack>
      </Card>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Archive ${groupName}?`}
        description={`Members will lose access immediately. The ${noun}'s posts, members, and meetings are preserved — you can restore it later from the archived list. There is no automatic delete.`}
        actionLabel={`Archive ${noun}`}
        cancelLabel={`Keep ${noun}`}
        destructive
        onAction={handleArchive}
      />
    </Stack>
  );
}

function RestorePanel({
  groupId,
  groupName,
  kind,
}: {
  groupId: string;
  groupName: string;
  kind: GroupKind;
}) {
  const restoreMutation = useRestoreGroup();
  const [error, setError] = useState<string | null>(null);
  const noun = NOUN[kind];

  const handleRestore = () => {
    setError(null);
    restoreMutation.mutate(groupId, {
      onError: (err) =>
        setError(err instanceof Error ? err.message : `Failed to restore ${noun}.`),
    });
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Restore"
        subtitle={`${groupName} is archived. Members can read past content but new posts, meetings, and invites are paused. Restore to make the ${noun} writable again — content was never deleted.`}
      />

      <Card pad="default">
        <Stack gap="lg">
          <Eyebrow tone="accent">What happens when you restore</Eyebrow>
          <Stack gap="sm">
            <Body size="sm" color="mute">
              · Members regain access to this {noun}
              {kind === 'circle' ? ' and its group chat' : ''}.
            </Body>
            <Body size="sm" color="mute">
              · The {noun} reappears in directories and search.
            </Body>
            <Body size="sm" color="mute">
              · Posts, members, and meetings resume from where they paused.
            </Body>
          </Stack>
          <Row>
            <Button
              variant="primary"
              size="md"
              onClick={handleRestore}
              loading={restoreMutation.isPending}
            >
              {`Restore ${noun}`}
            </Button>
          </Row>
          {error !== null ? (
            <span role="alert" aria-live="polite">
              <Body size="sm" color="destructive">{error}</Body>
            </span>
          ) : null}
        </Stack>
      </Card>
    </Stack>
  );
}
